import React from "react";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, ResponsiveContainer } from "recharts";
import {
  AppWindow, Bot, Cloud, Database, FileLock, Fingerprint, Globe, Laptop, ListChecks, Mail, Network, ShieldAlert, ShieldCheck,
} from "lucide-react";
import { formatAge, formatLocal, formatLocalDate, freshnessOf, staleSources } from "./socFreshness";
import { AREA_COLOR, NEUTRAL } from "./socChartTheme";
import { FOCUS_RING } from "./socUi";
import { areaHeadline, areaIssueParts, hasNoFigures } from "./socConsole";

export const AREA_ICON = {
  incidents: ShieldAlert,
  identity: Fingerprint,
  endpoint: Laptop,
  cloud: Cloud,
  cloudApps: AppWindow,
  siem: Database,
  detectionRules: ListChecks,
  network: Network,
  external: Globe,
  data: FileLock,
  email: Mail,
  agenticAi: Bot,
};

const has = (metrics, key) => Object.prototype.hasOwnProperty.call(metrics, key);

// A label inside a sentence: its first word lower-cased unless it is a name (Sentinel, Splunk, AWS…).
const NAMES = new Set(["Sentinel", "Splunk", "Microsoft", "Defender", "Exchange", "Fortinet", "AWS", "GCP", "AI", "VPN", "MFA", "CVE", "CVEs"]);
const sentenceCase = (label) => {
  const first = label.split(" ")[0];
  return NAMES.has(first) || first === first.toUpperCase() ? label : `${label.charAt(0).toLowerCase()}${label.slice(1)}`;
};

// `config.matrix` rows that have something to show: a cell's key must be one of the config's metrics
// (a sub-area view keeps only its own) and either came back or is pending. A row with neither is left out.
const matrixRows = (config, metrics, pending) => {
  const allowed = new Set(config.metrics.map(([key]) => key));
  const shown = (key) => key && allowed.has(key) && (has(metrics, key) || pending.includes(key));
  return config.matrix.rows
    .map((row) => ({ ...row, cells: row.keys.map((key) => (shown(key) ? key : null)) }))
    .filter((row) => row.cells.some(Boolean));
};

// The facts to show, in config order, under their group headings (one untitled group when the config
// has none). A group whose facts are all absent is left out.
const metricGroups = (config, metrics, pending) => {
  const shown = config.metrics.filter(([key]) => Object.prototype.hasOwnProperty.call(metrics, key) || pending.includes(key));
  if (!config.groups) return [{ title: null, rows: shown }];
  return config.groups
    .map((g) => ({ title: g.title, rows: shown.filter(([key]) => g.keys.includes(key)) }))
    .filter((g) => g.rows.length > 0);
};

export const STATUS_STYLES = {
  critical: { label: "Action needed", className: "bg-status-error-bg text-status-error" },
  warning: { label: "Review", className: "bg-status-warning-bg text-status-warning" },
  good: { label: "Healthy", className: "bg-status-success-bg text-status-success" },
  notConnected: { label: "Not connected", className: "bg-theme-secondary text-content-muted" },
  // Derived on the page: the area has received no new data for longer than its threshold, so its
  // counts may be out of date whatever they say.
  stale: { label: "Stale", className: "bg-status-warning-bg text-status-warning" },
  // Derived on the page: connected, but none of the area's figures has been reported yet.
  noFigures: { label: "No figures yet", className: "bg-theme-secondary text-content-secondary" },
};

const BUTTON_CLASS =
  "inline-flex items-center rounded-md border border-border px-3 py-1.5 text-sm font-medium " +
  "text-content-primary bg-theme-secondary hover:border-border-hover transition-colors";

const formatValue = (key, value, format, metrics) => {
  if (format) return format(value, metrics);
  return key === "mfaPercent" ? `${value}%` : String(value);
};

// One Security Operations area tile: a strip and icon in the area's colour, the status pill, one
// headline figure, the area's facts, a 20-day mini chart where the area has a daily series (`trend`),
// sync time and actions. Labels come only from `config`; metric keys the config doesn't know are
// ignored. A key in `tile.pending` (connected, figure not written yet) reads "Waiting for the next
// sync". `highlight` rings the tile (the posture ribbon jumped to it). `config.headline === false`
// (a sub-area view showing part of a tile) leaves the headline out. `linkAccess` is what the viewer
// may open (built by SocDashboard), passed to the tile's link: only the Data tile reads it.
// `config.groups` ([{ title, keys }]) lists the facts under headings; `config.sources` names each
// fact's source; with `onMetric`, the facts in `config.clickable` are buttons and `activeMetric` is
// the one open. `config.matrix` ({ columns, rows: [{ label, source, keys }], note }) shows the facts
// instead as a table, one row per source with one figure per column (External threat).
const SocTile = ({ tile, config, canConnect, now, trend = null, highlight = false, linkAccess = null, onMetric = null, activeMetric = null }) => {
  const navigate = useNavigate();
  const metrics = tile.metrics || {};
  const pending = tile.pending || [];
  const connected = tile.status !== "notConnected" && tile.connected !== false;
  const freshness = freshnessOf(tile, now || new Date());
  // A figure the area hasn't reported. "Next sync" would promise data that a stopped source won't send,
  // and reads oddly under a sync a few minutes old, so it says what is known instead.
  const pendingText = freshness.state === "stale" ? "Not reported: the source stopped sending" : "Not reported yet";
  const status = freshness.state === "stale"
    ? STATUS_STYLES.stale
    : connected && hasNoFigures(tile) ? STATUS_STYLES.noFigures
    : STATUS_STYLES[tile.status] || STATUS_STYLES.notConnected;
  const link = connected ? config.link?.(metrics, pending, linkAccess) ?? null : null;
  const notes = (config.notes || []).filter(([flag]) => metrics[flag] === 1);
  const color = AREA_COLOR[tile.key] || NEUTRAL;
  const Icon = AREA_ICON[tile.key] || ShieldCheck;
  const headline = config.headline === false ? null : areaHeadline(tile);
  const bars = connected && trend && trend.some((v) => v > 0) ? trend.slice(-20).map((v, i) => ({ i, v })) : null;

  return (
    <div data-testid={`soc-tile-${tile.key}`} className="h-full">
      <div
        className={`relative flex h-full flex-col overflow-hidden rounded-xl border bg-theme-card-solid p-4 pt-5 shadow-sm transition-[box-shadow,transform] duration-200 motion-safe:hover:-translate-y-0.5 hover:shadow-md ${
          highlight ? "border-accent-cyan ring-2 ring-accent-cyan" : "border-border"
        }`}
      >
        <span className="absolute inset-x-0 top-0 h-1" style={{ background: connected ? color : NEUTRAL }} aria-hidden="true" />
        <div className="flex items-start gap-2.5">
          <span
            className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-white"
            style={{ background: connected ? color : NEUTRAL }}
            aria-hidden="true"
          >
            <Icon className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <h2 className="text-base font-semibold leading-tight text-content-primary">{config.title}</h2>
            {connected && config.caption && (
              <p className="text-xs text-content-muted">{config.caption}</p>
            )}
          </div>
          <span className={`ml-auto shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${status.className}`}>
            {status.label}
          </span>
        </div>

        {connected ? (
          <>
            {headline && (
              <p className="mt-3 flex items-baseline gap-2" data-testid={`soc-tile-headline-${tile.key}`}>
                <span className="text-3xl font-extrabold tracking-tight tabular-nums text-content-primary">{headline[0]}</span>
                <span className="text-xs text-content-secondary">{headline[1]}</span>
              </p>
            )}
            {headline && !config.matrix && areaIssueParts(tile).length > 1 && (
              <p className="mt-0.5 text-[11px] leading-snug text-content-muted" data-testid={`soc-tile-sum-${tile.key}`}>
                {areaIssueParts(tile).map((p) => `${p.value} ${sentenceCase(p.label)}`).join(" + ")}
              </p>
            )}
            {bars && (
              <div className="mt-2 h-8" aria-hidden="true" data-testid={`soc-tile-trend-${tile.key}`}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={bars} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                    <Bar dataKey="v" fill={color} fillOpacity={0.75} radius={[2, 2, 0, 0]} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {config.matrix ? (
              <div className="mt-3 flex-1">
                <table className="w-full text-[13px]" data-testid={`soc-tile-matrix-${tile.key}`}>
                  <thead>
                    <tr className="text-[11px] uppercase tracking-wide text-content-muted">
                      <th scope="col" className="pb-1 text-left font-semibold">Source</th>
                      {config.matrix.columns.map((c) => (
                        <th key={c} scope="col" className="pb-1 pl-2 text-right font-semibold">{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {matrixRows(config, metrics, pending).map((row) => (
                      <tr key={row.label} className="border-t border-border align-top" data-testid="soc-tile-matrix-row">
                        <th scope="row" className="py-1 text-left font-normal">
                          <span className="block text-content-secondary">{row.label}</span>
                          <span className="block text-[11px] text-content-muted">{row.source}</span>
                        </th>
                        {row.cells.map((key, i) => {
                          const column = config.matrix.columns[i];
                          if (!key) {
                            return (
                              <td key={column} className="py-1 pl-2 text-right text-content-muted"
                                title={row.keys[i] ? "Not available" : `${row.source} doesn't raise risks`}>—</td>
                            );
                          }
                          if (!has(metrics, key)) {
                            return (
                              <td key={column} className="py-1 pl-2 text-right text-xs text-content-muted" data-testid={`soc-pending-${key}`}
                                title={pendingText}>…</td>
                            );
                          }
                          const value = String(metrics[key]);
                          if (onMetric && (config.clickable || []).includes(key)) {
                            const on = activeMetric === key;
                            return (
                              <td key={column} className="py-0.5 pl-2 text-right">
                                <button type="button" onClick={() => onMetric(key)} aria-pressed={on}
                                  aria-label={`${row.label}, ${column.toLowerCase()}: ${value}. Show them`}
                                  data-testid={`soc-metric-${key}`}
                                  title="Show the items behind this figure"
                                  className={`min-w-[2.5rem] rounded px-1.5 py-0.5 text-right font-medium tabular-nums text-content-primary underline decoration-dotted underline-offset-2 transition-colors hover:bg-theme-hover ${FOCUS_RING} ${
                                    on ? "bg-accent-cyan-muted" : ""}`}>
                                  {value}
                                </button>
                              </td>
                            );
                          }
                          return (
                            <td key={column} className="py-1 pl-2 text-right font-medium tabular-nums text-content-primary"
                              data-testid={`soc-metric-${key}`}>{value}</td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {config.matrix.note && (
                  <p className="mt-2 text-[11px] leading-snug text-content-muted" data-testid={`soc-tile-note-${tile.key}`}>{config.matrix.note}</p>
                )}
              </div>
            ) : (
            <dl className="mt-3 flex-1 space-y-1">
              {metricGroups(config, metrics, pending).map((group) => (
                <React.Fragment key={group.title || "all"}>
                  {group.title && (
                    <p className="pt-1 text-[11px] font-semibold uppercase tracking-wide text-content-muted first:pt-0"
                      data-testid={`soc-tile-group-${tile.key}`}>
                      {group.title}
                    </p>
                  )}
                  {group.rows.map(([key, label, format]) => {
                    const has = Object.prototype.hasOwnProperty.call(metrics, key);
                    const value = has ? (
                      <dd className="font-medium text-content-primary text-right">{formatValue(key, metrics[key], format, metrics)}</dd>
                    ) : (
                      <dd className="text-right text-xs text-content-muted" data-testid={`soc-pending-${key}`}>
                        {pendingText}
                      </dd>
                    );
                    const term = (
                      <dt className="text-content-secondary">
                        {label}:
                        {config.sources?.[key] && <span className="ml-1 text-[11px] text-content-muted">{config.sources[key]}</span>}
                      </dt>
                    );
                    if (onMetric && has && (config.clickable || []).includes(key)) {
                      const on = activeMetric === key;
                      return (
                        // A div (a <dl> may only hold div / dt / dd) acting as a button, keyboard included.
                        <div key={key} role="button" tabIndex={0} aria-pressed={on}
                          onClick={() => onMetric(key)}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onMetric(key); } }}
                          data-testid={`soc-metric-${key}`}
                          title="Show the items behind this figure"
                          className={`-mx-1 flex cursor-pointer justify-between gap-2 rounded px-1 text-[13px] transition-colors hover:bg-theme-hover ${FOCUS_RING} ${
                            on ? "bg-accent-cyan-muted" : ""}`}>
                          {term}{value}
                        </div>
                      );
                    }
                    return <div key={key} className="flex justify-between gap-2 text-[13px]">{term}{value}</div>;
                  })}
                </React.Fragment>
              ))}
            </dl>
            )}
            <div className="mt-3 space-y-0.5 text-xs text-content-muted">
              {notes.map(([flag, text]) => (
                <p key={flag} className="text-status-warning">{text}</p>
              ))}
              {freshness.state === "stale" && (
                <p className="text-status-warning">
                  No new data since {formatLocal(freshness.lastSync)} ({formatAge(freshness.ageMs)})
                </p>
              )}
              {freshness.state === "fresh" && <p>Last sync {formatLocal(freshness.lastSync)}</p>}
              {freshness.state === "fresh" && staleSources(tile, now || new Date()).map((s) => (
                <p key={s.name} className="text-status-warning" data-testid={`soc-tile-stopped-${tile.key}`}>
                  {`${s.name}: no new data since ${formatLocalDate(s.lastSync)} (${formatAge(s.ageMs)})`}
                </p>
              ))}
              {freshness.state === "unknown" && <p>Sync time unknown</p>}
              {tile.key === "external" && !config.matrix && <p>Events from the last 30 days</p>}
              {config.footnote && <p className="leading-snug" data-testid={`soc-tile-footnote-${tile.key}`}>{config.footnote}</p>}
            </div>
            {link && (
              <div className="mt-3">
                <button type="button" className={BUTTON_CLASS} onClick={() => navigate(link)}>
                  View details
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="mt-3 flex-1 text-sm text-content-secondary">
            <p>No data source is connected for this area.</p>
            {canConnect && config.connect ? (
              <div className="mt-3">
                <button
                  type="button"
                  className={BUTTON_CLASS}
                  onClick={() =>
                    navigate(
                      config.connect.path,
                      config.connect.state ? { state: config.connect.state } : undefined
                    )
                  }
                >
                  Connect
                </button>
              </div>
            ) : (
              <p className="mt-2 text-content-muted">
                Ask an organization administrator to connect a data source.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SocTile;
